/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Keeps a full-screen overlay inside the part of the screen the visitor can
 * actually see, and the field they are typing in visible inside it.
 *
 * `position: fixed; inset: 0` is sized to the LAYOUT viewport, and neither iOS
 * Safari nor Chrome on Android (whose default has been
 * `interactive-widget=resizes-visual` since Chrome 108) shrinks the layout
 * viewport for the on-screen keyboard — only the VISUAL viewport shrinks. Every
 * modal on the site therefore kept its full height with the keyboard over its
 * lower part, and because the page behind it is locked and the panel's scroll
 * range was computed for the whole screen, whatever sat under the keyboard
 * could not be scrolled into view at all. Measured 2026-10-01 on a 390×750
 * phone viewport: the signup form's step 1 was a 708 px panel holding 706 px of
 * content — no scroll range — so with a 336 px keyboard up, the e-mail field,
 * the phone field and "Volgende" were out of reach.
 *
 * `useVisualViewportFit` pins a modal's fixed viewport element to
 * `window.visualViewport` while it is mounted (top = offsetTop, height =
 * height), so the panel lays out — and scrolls — within what is visible.
 * Pinch-zoom is left alone: a magnified visual viewport is not a smaller screen.
 *
 * After every change, and whenever a field takes focus, the focused field is
 * brought into view inside its own scroller together with the label above it —
 * the question it answers. `revealField` sets `scrollTop` on that one scroller;
 * `scrollIntoView` would also try to move every ancestor, the locked page and
 * the visual viewport included.
 */

import { useEffect, type RefObject } from 'react';

const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

/** A field a phone opens its keyboard (or picker) for. */
export function isTextEntryField(el: Element | null): el is HTMLElement {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  if (el instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(el.type);
  return el instanceof HTMLElement && el.isContentEditable;
}

/** Breathing room kept between a revealed block and the scroller's edges. */
const EDGE_PX = 12;
/** Room left above a field whose question is not a programmatic label. */
const LABEL_ALLOWANCE_PX = 40;

/**
 * The element that asks the question a field answers: a radio's fieldset
 * legend, else the field's first <label>, else its `aria-labelledby` target.
 */
function questionFor(field: HTMLElement): HTMLElement | null {
  if (field instanceof HTMLInputElement && (field.type === 'radio' || field.type === 'checkbox')) {
    const legend = field.closest('fieldset')?.querySelector('legend');
    if (legend) return legend;
  }
  const labels = (field as HTMLInputElement).labels;
  if (labels && labels.length > 0) return labels[0];
  const labelledBy = field.getAttribute('aria-labelledby');
  if (labelledBy) {
    const el = document.getElementById(labelledBy.split(/\s+/)[0]);
    if (el) return el;
  }
  return null;
}

const isVisible = (el: Element): boolean => el.getClientRects().length > 0;

/**
 * The part of `scroller` a field can actually be seen in: its box minus any
 * sticky bar pinned to its top or bottom edge. The estimator pins its title
 * and its buttons inside the scrolling panel, so "inside the scroller" alone
 * left a focused field behind the button bar. Bars are looked for one and two
 * levels down — where every modal on the site keeps them.
 */
function visibleBand(scroller: HTMLElement): { top: number; bottom: number } {
  const frame = scroller.getBoundingClientRect();
  let top = frame.top + scroller.clientTop;
  let bottom = top + scroller.clientHeight;
  const edgeTop = top;
  const edgeBottom = bottom;
  for (const el of Array.from(scroller.querySelectorAll(':scope > *, :scope > * > *'))) {
    if (getComputedStyle(el).position !== 'sticky') continue;
    const box = el.getBoundingClientRect();
    if (box.height === 0) continue;
    if (Math.abs(box.top - edgeTop) < 2) top = Math.max(top, box.bottom);
    else if (Math.abs(box.bottom - edgeBottom) < 2) bottom = Math.min(bottom, box.top);
  }
  return { top, bottom };
}

/**
 * Scroll `scroller` — and nothing else — so `field` and its question are in
 * view. 'nearest' moves as little as possible; a block taller than the
 * scroller, or `align: 'start'`, puts the question at the top (a typeahead
 * whose suggestions open below it needs that room).
 */
export function revealField(
  field: HTMLElement,
  scroller: HTMLElement,
  align: 'nearest' | 'start' = 'nearest'
): void {
  const { top: viewTop, bottom: viewBottom } = visibleBand(scroller);
  const box = field.getBoundingClientRect();
  const question = questionFor(field);
  const questionTop =
    question && isVisible(question)
      ? question.getBoundingClientRect().top
      : box.top - LABEL_ALLOWANCE_PX;
  const top = Math.min(questionTop, box.top) - EDGE_PX;
  const bottom = box.bottom + EDGE_PX;
  let delta = 0;
  if (align === 'start' || top < viewTop || bottom - top > viewBottom - viewTop) {
    delta = top - viewTop;
  } else if (bottom > viewBottom) {
    delta = bottom - viewBottom;
  }
  if (Math.abs(delta) >= 1) scroller.scrollTop += delta;
}

/** The closest ancestor of `el`, inside `boundary`, that scrolls vertically. */
export function scrollerOf(el: HTMLElement, boundary: HTMLElement): HTMLElement | null {
  for (
    let node = el.parentElement;
    node && node !== boundary.parentElement;
    node = node.parentElement
  ) {
    const overflowY = getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return null;
}

/** Bring the focused field inside `container` (if any) into view in its scroller. */
export function revealFocusedField(container: HTMLElement): void {
  const focused = document.activeElement;
  if (!isTextEntryField(focused) || !container.contains(focused)) return;
  const scroller = scrollerOf(focused, container);
  if (!scroller) return;
  revealField(focused, scroller, focused.dataset.revealAlign === 'start' ? 'start' : 'nearest');
}

/**
 * Pin the fixed element behind `ref` to the visual viewport while mounted (and
 * `active`). SSR-safe and a no-op where `visualViewport` does not exist — the
 * element then simply keeps its `inset-0` from CSS.
 */
export function useVisualViewportFit(ref: RefObject<HTMLElement | null>, active = true): void {
  useEffect(() => {
    if (!active || typeof window === 'undefined') return;
    const el = ref.current;
    const vv = window.visualViewport;
    if (!el || !vv) return;

    let frame = 0;
    const clear = () => {
      el.style.top = '';
      el.style.bottom = '';
      el.style.height = '';
    };
    const fit = () => {
      frame = 0;
      if (vv.scale > 1.01) {
        clear();
      } else {
        el.style.top = `${vv.offsetTop}px`;
        el.style.bottom = 'auto';
        el.style.height = `${vv.height}px`;
      }
      revealFocusedField(el);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(fit);
    };

    fit();
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    // A field taking focus while the keyboard is already up resizes nothing:
    // let the browser scroll it first, then make sure its question shows too.
    el.addEventListener('focusin', schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
      el.removeEventListener('focusin', schedule);
      clear();
    };
  }, [ref, active]);
}
